import { Grid } from '@syncfusion/ej2-grids';

import { isBulkUpdateModel } from '@/shared/bulkUpdate/fields';
import { BulkUpdateSelection } from '@/shared/bulkUpdate/types';
import { GridFilterPredicate } from '@/shared/types/grid';
import { ModelName, ViewModels } from '@/shared/types/model';

export type BulkUpdateSelectionState = {
  selectAllFiltered: boolean;
  explicitIds: Set<string>;
  excludedIds: Set<string>;
};

const selectionStates: Record<string, BulkUpdateSelectionState> = {};

export const getOrCreateSelectionState = (gridId: string): BulkUpdateSelectionState => {
  if (!selectionStates[gridId]) {
    selectionStates[gridId] = {
      selectAllFiltered: false,
      explicitIds: new Set(),
      excludedIds: new Set(),
    };
  }
  return selectionStates[gridId];
};

export const resetSelectionState = (gridId: string) => {
  const state = getOrCreateSelectionState(gridId);
  state.selectAllFiltered = false;
  state.explicitIds.clear();
  state.excludedIds.clear();
};

export const getSelectedCount = (gridId: string, grid: Grid): number => {
  const state = getOrCreateSelectionState(gridId);

  if (state.selectAllFiltered) {
    const total = grid.totalDataRecordsCount ?? 0;
    return Math.max(0, total - state.excludedIds.size);
  }

  return state.explicitIds.size;
};

export const getGridFilterPredicates = (grid: Grid): GridFilterPredicate[] => {
  const filters = (grid.filterModule as any)?.actualPredicate;

  if (!filters) {
    return [];
  }

  return Object.keys(filters).flatMap((field) =>
    (filters[field] as (GridFilterPredicate & { matchCase?: boolean })[]).map((filter) => ({
      ...filter,
      ignoreCase: filter.ignoreCase ?? (filter.matchCase !== undefined ? !filter.matchCase : true),
    })),
  );
};

export const buildBulkUpdateSelection = (gridId: string, grid: Grid): BulkUpdateSelection | null => {
  const state = getOrCreateSelectionState(gridId);
  const count = getSelectedCount(gridId, grid);

  if (count === 0) {
    return null;
  }

  if (state.selectAllFiltered) {
    return {
      allFiltered: true,
      where: getGridFilterPredicates(grid),
      archived: grid.element.dataset.archived === 'true',
      excludedIds: [...state.excludedIds],
    };
  }

  return {
    ids: [...state.explicitIds],
  };
};

export const getBulkUpdateModelName = (grid: Grid): ModelName | null => {
  const modelName = grid.element.dataset.model as ModelName | undefined;
  if (!modelName) return null;

  const resolved = (ViewModels[modelName] ?? modelName) as ModelName;
  return isBulkUpdateModel(resolved) ? resolved : null;
};

export const supportsGridRowSelection = (grid: Grid): boolean => {
  return !!document.getElementById(`${grid.element.id}-bulk-update-bar`);
};

export const updateBulkUpdateBar = (gridId: string, grid: Grid) => {
  const bar = document.getElementById(`${gridId}-bulk-update-bar`);
  if (!bar) return;

  const count = getSelectedCount(gridId, grid);
  const countEl = bar.querySelector('.bulk-update-bar-count');

  if (countEl) {
    countEl.textContent = `${count} selected`;
  }

  if (count > 0) {
    bar.classList.remove('d-none');
    bar.classList.add('d-flex');
  } else {
    bar.classList.add('d-none');
    bar.classList.remove('d-flex');
  }
};

const addLoadedRowsToSelection = (grid: Grid, indexes: number[]) => {
  if (!indexes.length) return;

  const selectionModule = (grid as any).selectionModule as
    | { addRowsToSelection?: (indexes: number[]) => void }
    | undefined;
  if (selectionModule?.addRowsToSelection) {
    selectionModule.addRowsToSelection(indexes);
    return;
  }

  grid.selectRows([...new Set([...grid.getSelectedRowIndexes(), ...indexes])]);
};

export const selectAllVisibleRows = (grid: Grid) => {
  const selectedIndexes = new Set(grid.getSelectedRowIndexes());
  const rows = grid.getCurrentViewRecords() as { id?: string }[];
  const indexes: number[] = [];

  rows.forEach((row, index) => {
    if (row?.id && !selectedIndexes.has(index)) {
      indexes.push(index);
    }
  });

  addLoadedRowsToSelection(grid, indexes);
};

export const applySelectAllFilteredToLoadedRows = (gridId: string, grid: Grid) => {
  const state = getOrCreateSelectionState(gridId);
  if (!state.selectAllFiltered) return;

  const selectedIndexes = new Set(grid.getSelectedRowIndexes());
  const rows = grid.getCurrentViewRecords() as { id?: string }[];
  const indexes: number[] = [];

  rows.forEach((row, index) => {
    if (row?.id && !state.excludedIds.has(row.id) && !selectedIndexes.has(index)) {
      indexes.push(index);
    }
  });

  addLoadedRowsToSelection(grid, indexes);
};

export type BulkUpdateOpenHandler = (gridId: string, grid: Grid) => void;

let openHandler: BulkUpdateOpenHandler | null = null;

export const registerBulkUpdateOpenHandler = (handler: BulkUpdateOpenHandler) => {
  openHandler = handler;
};

export const initBulkUpdateBars = (getGrid: (gridId: string) => Grid | undefined) => {
  document.querySelectorAll<HTMLElement>('.bulk-update-bar').forEach((bar) => {
    const gridId = bar.dataset.gridId;
    if (!gridId) return;

    const button = bar.querySelector<HTMLButtonElement>('[data-bulk-update-open]');
    button?.addEventListener('click', () => {
      const grid = getGrid(gridId);
      if (grid && openHandler) {
        openHandler(gridId, grid);
      }
    });
  });
};
